<template>
  <div :id="`${source}_bulk_assign_notes`">
    <a-button
      @click="handleAddNoteClick"
      class="secondary_small gap-1 flex items-center justify-center"
    >
      <ThemeImage class="size-4" :icon="imageNames.NOTE_GREY" alt="add_note" />
      Add Note
    </a-button>

    <AddNotesModal
      v-model:visible="isNotesModalVisible"
      :source="source"
      @confirm="handleAddNote"
      :loading="isLoading"
    />
  </div>
</template>

<script setup>
import AddNotesModal from "@/src/components/custom_table_components/AddNotesModal.vue";
import { computed, ref } from "vue";
import { message } from "ant-design-vue";
import DashboardService from "@/src/api/DashboardService.js";
import { formatEntityTypeText } from "@/src/utils/utils.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";

const props = defineProps({
  source: { type: String, required: true },
  selection: { type: Object, required: true },
  selectCount: { type: Number, required: true },
  viewId: { type: Number, default: null },
  listId: { type: Number, default: null },
  searchQuery: { type: String, default: "" },
});

const emit = defineEmits(["bulk-assigned-notes"]);

const isNotesModalVisible = ref(false);
const isLoading = ref(false);

const entityText = computed(() =>
  formatEntityTypeText({
    entityType: props.source,
    count: props.selectCount,
  }),
);

const handleAddNoteClick = () => {
  isNotesModalVisible.value = true;
};

const handleAddNote = async (data) => {
  try {
    isLoading.value = true;

    let response = {};

    const content = data?.notes || "";
    const attachmentIds = data?.attachments?.map((a) => a.id) || [];

    const payload = {
      content,
      attachment_ids: attachmentIds,
      select_all: props.selection.select_all,
      selected_ids: props.selection.selected_ids,
      unselected_ids: props.selection.unselected_ids,
    };

    if (props.viewId) {
      payload.view_id = props.viewId;
    }

    if (props?.listId) {
      payload.list_id = props.listId;
    }

    if (props.searchQuery && props.searchQuery.trim() !== "") {
      payload.search_query = props.searchQuery;
    }

    if (props.source === entityTypes.CONTACT) {
      response = await DashboardService.addMultipleContactNotes(payload);
    } else if (props.source === entityTypes.LEAD) {
      response = await DashboardService.addMultipleLeadNotes(payload);
    } else if (props.source === entityTypes.COMPANY) {
      response = await DashboardService.addMultipleCompanyNotes(payload);
    }

    if (response?.success) {
      message.success(`Notes for ${entityText.value} added successfully`);
      isNotesModalVisible.value = false;
      emit("bulk-assigned-notes");
    } else {
      message.error("Failed to add notes");
    }
  } catch (error) {
    console.error("Error adding notes:", error);
    message.error("An error occurred while adding notes");
  } finally {
    isLoading.value = false;
  }
};
</script>
